import { useRef } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import SelectionButton from '~/bootcamp/Activities/components/SelectionButton'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionImproperToMixedChoose from './useFractionImproperToMixedChoose'

interface FractionPropsModel {
  numerator?: number
  denominator?: number
  dividerClassName?: string
  quotient?: number
}

function Fraction({ numerator, denominator, quotient, dividerClassName = '' }: FractionPropsModel): React.ReactElement {
  return (
    <div className="flex items-center space-x-1">
      {!!quotient && <span className="text-2xl">{quotient}</span>}
      <div className="flex flex-col items-center">
        <span className="text-2xl">{numerator}</span>
        <Divider className={dividerClassName} />
        <span className="text-2xl">{denominator}</span>
      </div>
    </div>
  )
}

function FractionImproperToMixedChoose({
  answerState,
  actionButtonsFC,
  autofocus = true,
  onAnswerStateChange,
  onNextQuestionClick,
  onInstanceChange,
  difficultyLevel,
}: ActivityProps): React.ReactElement {
  const { question, options, formValid, selectedAnswer, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useFractionImproperToMixedChoose({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
    })

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const mixedOptions = (options || [])
    .map(
      ({ quotient, numerator, denominator }) => `${quotient ? `${quotient} ` : ''}\\frac{${numerator}}{${denominator}}`,
    )
    .join(', ')
  useEmitInstance(
    onInstanceChange,
    question && options?.length
      ? `\\frac{${question.numerator}}{${question.denominator}}; options: ${mixedOptions}`
      : null,
  )

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <Fraction denominator={question?.denominator} numerator={question?.numerator} />
      </FractionsActivityWrapper>
      <div className="flex justify-around space-x-2 sm:space-x-8 w-full">
        {options &&
          options.map(({ quotient, numerator, denominator }, index) => (
            <SelectionButton
              className="p-2 flex-1 flex justify-center"
              isSelected={selectedAnswer === index}
              isWrong={selectedAnswer === index && answerState === false}
              key={`${quotient}-${numerator}-${denominator}`}
              onClick={onInputChange.bind(null, index)}
            >
              <Fraction
                denominator={denominator}
                dividerClassName={selectedAnswer === index ? 'bg-white' : ''}
                numerator={numerator}
                quotient={quotient}
              />
            </SelectionButton>
          ))}
      </div>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionImproperToMixedChoose
